import { ChevronDown, Pencil } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TONE = {
  info: "bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]",
  muted: "bg-muted text-muted-foreground",
  danger: "bg-[hsl(var(--danger)/0.1)] text-[hsl(var(--danger))]",
  warning: "bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]",
};

// A pill that WRAPS long text instead of overflowing (unlike the global .chip which is nowrap).
function Tag({ tone = "muted", children }) {
  return <span className={`inline-block max-w-full whitespace-normal [overflow-wrap:anywhere] rounded-md px-2 py-1 text-[11px] leading-snug ${TONE[tone]}`}>{children}</span>;
}

function Group({ label, items, tone }) {
  if (!items?.length) return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">{items.map((t, i) => <Tag key={i} tone={tone}>{t}</Tag>)}</div>
    </div>
  );
}

export function CriteriaPanel({ icp, onEdit, children }) {
  const req = icp.required_conditions?.length || 0;
  const pref = icp.preferences?.length || 0;
  const exc = icp.exclusions?.length || 0;
  const summary = [req && `${req} required`, pref && `${pref} preferred`, exc && `${exc} excluded`].filter(Boolean).join(" · ") || "No criteria set";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="discover-criteria-toggle"
          className="panel w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-muted/50 text-left">
          <div className="min-w-0">
            <div className="section-title">Criteria</div>
            <div className="meta truncate">{summary}</div>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={6} className="w-80 max-h-[70vh] overflow-y-auto p-3 space-y-3" data-testid="discover-criteria">
        <div className="flex items-center justify-between">
          <span className="section-title">Criteria for {icp.name}</span>
          <button className="meta inline-flex items-center gap-1 hover:text-foreground shrink-0" onClick={onEdit} data-testid="discover-criteria-edit"><Pencil className="h-3 w-3" />Edit</button>
        </div>
        <Group label="Required" items={icp.required_conditions} tone="info" />
        <Group label="Preferred" items={icp.preferences} tone="muted" />
        <Group label="Exclude" items={icp.exclusions} tone="danger" />
        {icp.things_to_verify?.length > 0 && (
          <div className="rounded-md border border-dashed border-border p-2">
            <Group label="Verify in conversation" items={icp.things_to_verify} tone="warning" />
          </div>
        )}
        {children && <div className="pt-1 border-t border-border">{children}</div>}
      </PopoverContent>
    </Popover>
  );
}
